import { useMemo, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useClassDetails } from './hooks/useClassDetails'
import type { ClassBankRow } from '../../types/bruktbilfinansiering'

// Primary-tinted palette so the chart matches nybilstats design tokens.
const NEUTRAL_COLORS = [
  '#94a3b8', '#cbd5e1', '#a8a29e', '#9ca3af', '#d6d3d1',
  '#a3a3a3', '#78716c', '#737373', '#6b7280',
]
const SELF_COLOR = '#0891b2'   // primary-600 (cyan) — matches nybilstats user-bank
const ANDRE_COLOR = '#e5e7eb'

const fmtNum = (n: number) => new Intl.NumberFormat('nb-NO').format(n)
const fmtNok = (n: number | null) =>
  n === null || n === undefined
    ? '–'
    : new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(n)
const fmtPct = (n: number) => `${n.toFixed(1)} %`

type PieMetric = 'count' | 'volume'

interface PieSlice {
  name: string
  value: number
  color: string
  isSelf: boolean
  isAndre: boolean
}

const PIE_TOP_N = 9

function buildPieSlices(rows: ClassBankRow[], metric: PieMetric, othersLabel: string): PieSlice[] {
  const valueOf = (r: ClassBankRow) => (metric === 'count' ? r.loan_count : Number(r.total_belop))
  if (rows.length === 0) return []
  const ranked = [...rows].sort((a, b) => valueOf(b) - valueOf(a))
  const selfIndex = ranked.findIndex(r => r.is_self)
  const visible: ClassBankRow[] = []
  const cap = selfIndex >= 0 && selfIndex >= PIE_TOP_N ? PIE_TOP_N - 1 : PIE_TOP_N
  for (let i = 0; i < ranked.length && visible.length < cap; i++) {
    if (selfIndex >= PIE_TOP_N && ranked[i].is_self) continue
    visible.push(ranked[i])
  }
  if (selfIndex >= PIE_TOP_N) visible.push(ranked[selfIndex])

  const visibleSet = new Set(visible.map(r => r.bank_org_nr))
  const andreValue = ranked
    .filter(r => !visibleSet.has(r.bank_org_nr))
    .reduce((acc, r) => acc + valueOf(r), 0)

  let neutralIdx = 0
  const slices: PieSlice[] = visible.map(r => ({
    name: r.bank_name,
    value: valueOf(r),
    color: r.is_self ? SELF_COLOR : NEUTRAL_COLORS[neutralIdx++ % NEUTRAL_COLORS.length],
    isSelf: r.is_self,
    isAndre: false,
  }))
  if (andreValue > 0) {
    slices.push({ name: othersLabel, value: andreValue, color: ANDRE_COLOR, isSelf: false, isAndre: true })
  }
  return slices
}

export default function KlasserDetailsPage() {
  const { t } = useTranslation('bbf')
  const { bankOrgNr = '', classLabel = '' } = useParams<{ bankOrgNr: string; classLabel: string }>()
  const [searchParams] = useSearchParams()

  const startDate = searchParams.get('start_date') ?? `${new Date().getFullYear()}-01-01`
  const endDate = searchParams.get('end_date') ?? (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })()
  const varebilParam = searchParams.get('varebil')
  const varebil = varebilParam === 'true' ? true : varebilParam === 'false' ? false : undefined
  const buyerPostnummer = searchParams.get('buyer_postnummer') ?? undefined
  const dealerPostnummer = searchParams.get('dealer_postnummer') ?? undefined

  const { data, loading, error } = useClassDetails(bankOrgNr, classLabel, {
    startDate,
    endDate,
    varebil,
    buyerPostnummer,
    dealerPostnummer,
  })

  const [pieMetric, setPieMetric] = useState<PieMetric>('count')
  const [sortKey, setSortKey] = useState<keyof Pick<ClassBankRow,
    'rank' | 'loan_count' | 'share_count_pct' | 'total_belop' | 'share_volume_pct' | 'avg_belop'
  >>('rank')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const sortedRows = useMemo(() => {
    if (!data) return []
    const rows = [...data.rows]
    rows.sort((a, b) => {
      const va = a[sortKey] ?? 0
      const vb = b[sortKey] ?? 0
      const dir = sortDir === 'asc' ? 1 : -1
      return ((va as number) - (vb as number)) * dir
    })
    return rows
  }, [data, sortKey, sortDir])

  const pieSlices = useMemo(
    () => (data ? buildPieSlices(data.rows, pieMetric, t('shared.others')) : []),
    [data, pieMetric, t],
  )

  if (loading) return <div className="p-6 text-gray-500">{t('classDetails.loading')}</div>
  if (error) return <div className="p-6 text-red-600">{t('shared.errorLabel')} {error}</div>
  if (!data) return null

  const { kpis } = data
  const backUrl = `/bruktbil-finansiering/${bankOrgNr}?${searchParams.toString()}`

  function handleSort(key: typeof sortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'rank' ? 'asc' : 'desc')
    }
  }

  function SortHeader({ col, label }: { col: typeof sortKey; label: string }) {
    const active = sortKey === col
    return (
      <th
        onClick={() => handleSort(col)}
        className="px-3 py-2 text-right cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <span className="flex items-center justify-end gap-1">
          {label}
          {active ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ↕'}
        </span>
      </th>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Link
          to={backUrl}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-4 h-4" /> {t('classDetails.back')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('classDetails.title', { label: classLabel })}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">{data.bank.org_name}</p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
        <span className="font-medium">{t('classDetails.activeFilters')}</span>{' '}
        {t('classDetails.period', { from: startDate, to: endDate })}
        {varebilParam === 'true' && ` · ${t('shared.vehicle.varebil')}`}
        {varebilParam === 'false' && ` · ${t('shared.vehicle.personbil')}`}
        {buyerPostnummer && ` · ${t('classDetails.buyerPostnr', { postnr: buyerPostnummer })}`}
        {dealerPostnummer && ` · ${t('classDetails.dealerPostnr', { postnr: dealerPostnummer })}`}
        <span className="block text-xs mt-1">
          {t('classDetails.marketShareNote')}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('classDetails.transfersInClass')} value={fmtNum(kpis.total_transfers)} />
        <KpiCard
          label={t('shared.bankLoans')}
          value={`${fmtNum(kpis.bank_loan_count)}${kpis.bank_rank_by_count ? ` (${t('classDetails.rankOf', { rank: kpis.bank_rank_by_count, total: kpis.distinct_named_banks })})` : ''}`}
          highlight
        />
        <KpiCard label={t('shared.marketShareCount')} value={fmtPct(kpis.bank_share_count_pct)} highlight />
        <KpiCard label={t('shared.marketShareVolume')} value={fmtPct(kpis.bank_share_volume_pct)} highlight />
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('classDetails.banksShare')}</h2>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
            <button
              onClick={() => setPieMetric('count')}
              className={`px-3 py-1.5 ${pieMetric === 'count'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              {t('shared.count')}
            </button>
            <button
              onClick={() => setPieMetric('volume')}
              className={`px-3 py-1.5 ${pieMetric === 'volume'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
            >
              {t('shared.volume')}
            </button>
          </div>
        </div>
        {pieSlices.length === 0 ? (
          <p className="text-gray-500">{t('classDetails.noPie')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={440}>
            <PieChart margin={{ top: 16, right: 120, bottom: 16, left: 120 }}>
              <Pie
                data={pieSlices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={130}
                paddingAngle={1}
                label={(d: PieSlice & { percent?: number }) => {
                  const pct = (d.percent ?? 0) * 100
                  // Skip in-chart labels for small slices; they're still in the legend + tooltip.
                  // Always label the user's own bank so it's easy to find.
                  if (!d.isSelf && pct < 4) return ''
                  return `${d.name.split(' ')[0]} ${pct.toFixed(0)} %`
                }}
                labelLine={(props: { percent?: number; payload?: PieSlice } & Record<string, unknown>) => {
                  const pct = (props.percent ?? 0) * 100
                  const isSelf = props.payload?.isSelf ?? false
                  if (!isSelf && pct < 4) return <></>
                  const { points } = props as unknown as { points: { x: number; y: number }[] }
                  if (!points || points.length < 2) return <></>
                  return (
                    <polyline
                      points={points.map(p => `${p.x},${p.y}`).join(' ')}
                      stroke="#9ca3af"
                      fill="none"
                      strokeWidth={1}
                    />
                  )
                }}
              >
                {pieSlices.map(s => (
                  <Cell key={s.name} fill={s.color} stroke={s.isSelf ? '#0b1d6b' : undefined} strokeWidth={s.isSelf ? 2 : 1} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v: number) => (pieMetric === 'count' ? fmtNum(v) : fmtNok(v))}
              />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ fontSize: 12, lineHeight: '18px', paddingLeft: 12 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">{t('shared.allBanks')}</h2>
        <div className="overflow-x-auto card p-0">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <SortHeader col="rank" label="#" />
                <th className="text-left">{t('shared.bank')}</th>
                <SortHeader col="loan_count" label={t('shared.loansCount')} />
                <SortHeader col="share_count_pct" label={t('classDetails.shareCount')} />
                <SortHeader col="total_belop" label={t('shared.totalVolume')} />
                <SortHeader col="share_volume_pct" label={t('classDetails.shareVolume')} />
                <SortHeader col="avg_belop" label={t('shared.avgAmount')} />
              </tr>
            </thead>
            <tbody>
              {/* Totals row — sticky-styled, always first row */}
              {sortedRows.length > 0 && (() => {
                const totalLoanCount = sortedRows.reduce((acc, r) => acc + r.loan_count, 0)
                const totalBelop = sortedRows.reduce((acc, r) => acc + Number(r.total_belop ?? 0), 0)
                const avgBelop = totalLoanCount > 0 ? totalBelop / totalLoanCount : 0
                return (
                  <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                    <td className="text-right">∑</td>
                    <td>TOTAL</td>
                    <td className="text-right tabular-nums">{fmtNum(totalLoanCount)}</td>
                    <td className="text-right tabular-nums">{fmtPct(100)}</td>
                    <td className="text-right tabular-nums">{fmtNum(totalBelop)}</td>
                    <td className="text-right tabular-nums">{fmtPct(100)}</td>
                    <td className="text-right tabular-nums">{fmtNum(avgBelop)}</td>
                  </tr>
                )
              })()}
              {sortedRows.map((row, idx) => (
                <tr
                  key={row.bank_org_nr}
                  className={
                    row.is_self
                      ? 'bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/40 font-medium'
                      : `${idx % 2 === 0
                          ? 'bg-white dark:bg-gray-900/40'
                          : 'bg-gray-50 dark:bg-gray-800/60'} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`
                  }
                >
                  <td className="text-right tabular-nums">{row.rank}</td>
                  <td>{row.bank_name}</td>
                  <td className="text-right tabular-nums">{fmtNum(row.loan_count)}</td>
                  <td className="text-right tabular-nums">{fmtPct(row.share_count_pct)}</td>
                  <td className="text-right tabular-nums">{fmtNum(Number(row.total_belop ?? 0))}</td>
                  <td className="text-right tabular-nums">{fmtPct(row.share_volume_pct)}</td>
                  <td className="text-right tabular-nums">{row.avg_belop != null ? fmtNum(Number(row.avg_belop)) : '–'}</td>
                </tr>
              ))}
              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('classDetails.noRows')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function KpiCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`border rounded-lg p-3 ${
      highlight
        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</div>
    </div>
  )
}
